import React, { memo } from "react";

import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";

import { CONTACT_WIZARD_SAMPLE } from "../consts";
import { getContactName, getInitials, isCompanyContact } from "../helpers";
import { ContactWizardSceneProps } from "../types";

const CONTACT_TYPES = [
  { id: "C", label: "Tvrtka" },
  { id: "P", label: "Osoba" },
];

/** Korak "Detalji": kartica kontakta s vrstom, OIB-om i grupama. */
const InfoScene: React.FC<ContactWizardSceneProps> = ({ formValues }) => {
  const isCompany = isCompanyContact(formValues);
  const name = getContactName(formValues);
  const oib = formValues.personalIdentificationNumber || CONTACT_WIZARD_SAMPLE.oib;
  const groupCount = formValues.contactGroupIds?.length ?? 0;
  const currentTypeId = isCompany ? "C" : "P";

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Kontakt</div>
          <div className="mt-3 flex items-center gap-3">
            <span className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-sm font-bold text-white flex-shrink-0">
              {getInitials(name)}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-bold text-slate-800 truncate">{name}</div>
              <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-blue-50 text-[0.6rem] font-semibold text-blue-600">
                {isCompany ? "Tvrtka" : "Osoba"}
              </span>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-[0.7rem]">
              <span className="text-slate-500">OIB</span>
              <span className="font-semibold text-slate-800 truncate">{oib}</span>
            </div>
            {!isCompany && formValues.jobTitle && (
              <div className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-[0.7rem]">
                <span className="text-slate-500">Radno mjesto</span>
                <span className="font-semibold text-slate-800 truncate">{formValues.jobTitle}</span>
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {groupCount > 0 ? (
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-[0.6rem] font-semibold text-amber-700">
                Grupe: {groupCount}
              </span>
            ) : (
              CONTACT_WIZARD_SAMPLE.groups.map((group) => (
                <span key={group} className="px-2 py-0.5 rounded-full bg-amber-100 text-[0.6rem] font-semibold text-amber-700">
                  {group}
                </span>
              ))
            )}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-xs font-bold text-slate-800">Vrsta kontakta</div>
          <div className="mt-3 space-y-2">
            {CONTACT_TYPES.map((type) => (
              <div
                key={type.id}
                className={
                  type.id === currentTypeId
                    ? "flex items-center justify-between rounded-md bg-blue-600 px-3 py-2 text-[0.7rem] font-semibold text-white"
                    : "flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-[0.7rem] text-slate-500"
                }
              >
                <span>{type.label}</span>
                {type.id === currentTypeId && <span className="w-2 h-2 rounded-full bg-white" />}
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Kontakt je odmah spreman"
          subtitle="Za ponude, ugovore i račune"
          iconClassName="bg-blue-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(InfoScene);
